import { Routes } from '@angular/router';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './services/auth.service';

// Auth Guard - Waits for Firebase Auth to resolve, then protects routes
export const authGuard = async () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    await authService.authReady;

    if (authService.isAuthenticated()) {
        return true;
    }

    return router.createUrlTree(['/login']);
};

// Login Guard - Waits for Firebase Auth to resolve, then redirects authenticated users
export const loginGuard = async () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    await authService.authReady;

    if (!authService.isAuthenticated()) {
        return true;
    }

    return router.createUrlTree(['/home']);
};

// Super-Admin Guard
export const superAdminGuard = async () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    await authService.authReady;
    if (authService.isSuperAdmin()) return true;
    return router.createUrlTree(['/home']);
};

// Admin Guard - blocks 'user' role from admin-only pages (create-task, bulk-upload)
export const adminGuard = async () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    await authService.authReady;
    if (authService.isAdminOrAbove()) return true;
    return router.createUrlTree(['/home']);
};

export const routes: Routes = [
    { 
        path: '', 
        loadComponent: () => import('./components/home/home.component').then(m => m.HomeComponent),
        canActivate: [loginGuard]
    },
    { 
        path: 'login', 
        loadComponent: () => import('./components/login/login.component').then(m => m.LoginComponent),
        canActivate: [loginGuard]
    },
    {
        path: 'dashboard',
        loadComponent: () => import('./components/dashboard/dashboard.component').then(m => m.DashboardComponent),
        canActivate: [authGuard, adminGuard]
    },
    {
        path: 'calendar',
        loadComponent: () => import('./components/dashboard/dashboard.component').then(m => m.DashboardComponent),
        canActivate: [authGuard],
        data: { view: 'calendar' }
    },
    {
        path: 'home',
        loadComponent: () => import('./components/home-dashboard/home-dashboard.component').then(m => m.HomeDashboardComponent),
        canActivate: [authGuard]
    },
    {
        path: 'filter-search',
        loadComponent: () => import('./components/schedule-viewer/schedule-viewer.component').then(m => m.ScheduleViewerComponent),
        canActivate: [authGuard]
    },
    {
        path: 'create-task',
        loadComponent: () => import('./components/create-task/create-task.component').then(m => m.CreateTaskComponent),
        canActivate: [adminGuard]
    },
    {
        path: 'bulk-upload',
        loadComponent: () => import('./components/bulk-upload/bulk-upload.component').then(m => m.BulkUploadComponent),
        canActivate: [adminGuard]
    },
    {
        path: 'admin',
        loadComponent: () => import('./components/admin/admin.component').then(m => m.AdminComponent),
        canActivate: [superAdminGuard]
    },
    { path: '**', redirectTo: '/home' }
];
